import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { firstValueFrom, forkJoin, map, Observable, of, timeout } from "rxjs";
import { getUserConsents, getUserIncome, getUserSettings } from "src/app/api/smart-pensjon-rest-api/functions";
import {
  ConsentsDto,
  ErrorRepresentation,
  IncomeRepresentation,
  UserSettingsRepresentation,
} from "src/app/api/smart-pensjon-rest-api/models";
import { StrictHttpResponse } from "src/app/api/smart-pensjon-rest-api/strict-http-response";
import { SKIP_HTTP_CACHE } from "src/app/interceptors/caching.interceptor";
import { EXPECTED_401 } from "src/app/interceptors/keycloak-bearer.interceptor";
import {
  getDefaultStepKeys,
  getShouldOpen,
  getShouldShowPublicPensionStep,
  OnboardingStepKey,
} from "src/app/services/onboarding.service";
import { SessionSalaryService } from "src/app/services/session-salary.service";
import { hasNonBooleanValuesInArray } from "src/app/utils/object";
import { buildRestApiRootUrl } from "src/app/utils/rest-api-root-url";
import { getIsNotNullable } from "src/app/utils/utils";

/** Every card click waits for these requests, so give up early. ensureLoaded() waits 30 seconds. */
const ONBOARDING_DECISION_TIMEOUT = 10_000;

/** Which consent popup to show before navigating. */
export enum ConsentFlow {
  StorageAndNorskPensjon = "StorageAndNorskPensjon",
  GroupAndMarketing = "GroupAndMarketing",
  None = "None",
}

interface RestResponse<T> {
  errors?: Array<ErrorRepresentation>;
  payload?: T;
}

type RestRequest<T> = (
  http: HttpClient,
  rootUrl: string,
  params?: undefined,
  context?: HttpContext,
) => Observable<StrictHttpResponse<RestResponse<T>>>;

/**
 * Decides the onboarding popups with the same rules as before, from GET /user/consents, GET /user/income and
 * GET /user/settings.
 */
@Injectable({ providedIn: "root" })
export class OnboardingDecisionService {
  private readonly http = inject(HttpClient);
  private readonly sessionSalaryService = inject(SessionSalaryService);

  /**
   * The onboarding steps to show, in order. Empty when the popup should not open.
   * Sends GET /user/consents, /user/income and /user/settings on every call. There is no cache, so a save in the
   * popup counts on the next click. Resolves null when a request fails (also a 401 below auth level 3), they take
   * over 10 seconds, a payload is missing, or a response has errors. The caller then navigates without a popup.
   * Never rejects.
   * A caller that read GET /user/consents earlier in the same user action passes that answer as `readConsents`.
   * Then only GET /user/income and /user/settings are sent.
   */
  public async fetchOnboardingSteps(readConsents?: ConsentsDto): Promise<OnboardingStepKey[] | null> {
    try {
      const [consents, income, settings] = await firstValueFrom(
        forkJoin([
          readConsents ? of(readConsents) : this.fetchPayload(getUserConsents),
          this.fetchPayload(getUserIncome),
          this.fetchPayload(getUserSettings),
        ]).pipe(timeout(ONBOARDING_DECISION_TIMEOUT)),
      );
      return this.getOnboardingStepKeys(consents, income, settings);
    } catch {
      return null;
    }
  }

  /**
   * The consent popup to show. Sends GET /user/consents on every call. Resolves null in the same cases as
   * fetchOnboardingSteps(). Never rejects.
   */
  public async fetchConsentFlow(): Promise<ConsentFlow | null> {
    try {
      const consents = await firstValueFrom(
        this.fetchPayload(getUserConsents).pipe(timeout(ONBOARDING_DECISION_TIMEOUT)),
      );
      return getConsentFlow(consents);
    } catch {
      return null;
    }
  }

  private fetchPayload<T extends object>(request: RestRequest<T>): Observable<T> {
    return request(
      this.http,
      buildRestApiRootUrl(),
      undefined,
      new HttpContext().set(SKIP_HTTP_CACHE, true).set(EXPECTED_401, true),
    ).pipe(map((response) => getPayloadWithoutErrors(response.body)));
  }

  private getOnboardingStepKeys(
    consents: ConsentsDto,
    income: IncomeRepresentation,
    settings: UserSettingsRepresentation,
  ): OnboardingStepKey[] {
    const dataStorageConsent = consents.hasCustomerSuppliedDataConsent;
    const norskPensjonConsent = consents.hasNorskPensjonConsent;
    const hasSalary = getIsNotNullable(income.annualSalary) || this.hasSessionSalary();
    const shouldShowPublicPension = getShouldShowPublicPensionStep([
      income.hasActivePublicPensionContract === true,
      consents.hasNavConsent ?? undefined,
    ]);

    return getShouldOpen(dataStorageConsent, norskPensjonConsent, hasSalary, shouldShowPublicPension)
      ? getDefaultStepKeys(
          hasSalary,
          shouldShowPublicPension,
          dataStorageConsent,
          norskPensjonConsent,
          settings.hasPublicPension === true,
        )
      : [];
  }

  /**
   * The browser can hold a salary the server doesn't have: one entered without the storage consent, or by an
   * advisor in incognito mode. The session storage copy is read too, because the profile may not be loaded yet.
   */
  private hasSessionSalary(): boolean {
    return this.sessionSalaryService.readSalary() !== null;
  }
}

/**
 * The storage and Norsk Pensjon consent step when one of them is not answered. Else the group and marketing
 * consents when the backend says they are due.
 */
function getConsentFlow(consents: ConsentsDto): ConsentFlow {
  if (hasNonBooleanValuesInArray([consents.hasCustomerSuppliedDataConsent, consents.hasNorskPensjonConsent])) {
    return ConsentFlow.StorageAndNorskPensjon;
  }
  return consents.groupAndMarketingConsentDue === true ? ConsentFlow.GroupAndMarketing : ConsentFlow.None;
}

/** Throws when the payload is missing or the response or the payload has errors. */
function getPayloadWithoutErrors<T extends object>(body: RestResponse<T> | null): T {
  const payload = body?.payload;
  if (!payload || (body?.errors?.length ?? 0) > 0 || hasPayloadErrors(payload)) {
    throw new Error("The response has no usable payload");
  }
  return payload;
}

function hasPayloadErrors(payload: object): boolean {
  return "errors" in payload && Array.isArray(payload.errors) && payload.errors.length > 0;
}
